import React, { SetStateAction } from 'react';

import { Box } from '@mui/material';
import { Button } from '@apigrowfinance/pbolt-button';
import { Card } from '@apigrowfinance/pbolt-card';
import IllustratedMessage from '@apigrowfinance/pbolt-illustrated-message';
import { Restricted } from '@apigrowfinance/pbolt-permission-provider';
import RowItem from '@apigrowfinance/pbolt-row-item';
import { RiEqualizerLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';

import { FlexControlsResponse } from '@clients/domains/flexControls/flexControlsClient';
import LoadingErrorMessage from '@components/LoadingErrorMessage/LoadingErrorMessage';
import FlexControlItem from '@pages/accountDetail/tabs/controls/FlexControlItem';

interface ControlsProps {
    data: FlexControlsResponse;
    isLoading: boolean;
    isError: boolean;
    noResults: boolean;
    selectedCustomerId: number;
    setControlTypeModalOpen: React.Dispatch<SetStateAction<boolean>>;
}

const Controls = ({
    data,
    isLoading,
    noResults,
    isError,
    selectedCustomerId,
    setControlTypeModalOpen,
}: ControlsProps) => {
    if (noResults) {
        return (
            <Card isBottomRadiusBorder={false} isTopRadiusBorder={false}>
                <IllustratedMessage
                    icon={<RiEqualizerLine />}
                    messageMain={
                        <FormattedMessage
                            id="Controls.noneFound"
                            defaultMessage="No controls to show"
                        />
                    }
                    messageSub={
                        <>
                            <Box
                                component="span"
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}>
                                <FormattedMessage
                                    id="Controls.noneFoundDescription1"
                                    defaultMessage="No controls have been added to this account."
                                />
                            </Box>
                            <Restricted to="create.flexControl">
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}>
                                    <FormattedMessage
                                        id="Controls.noneFoundDescription2"
                                        defaultMessage="To add a control, click the button below."
                                    />
                                </Box>
                            </Restricted>
                        </>
                    }
                    callToAction={
                        <Restricted to="create.flexControl">
                            <Button
                                data-testid="add-control-button"
                                variant="contained"
                                onClick={() => {
                                    setControlTypeModalOpen(true);
                                }}>
                                <FormattedMessage
                                    id="Controls.addControlButton"
                                    defaultMessage="Add control"
                                />
                            </Button>
                        </Restricted>
                    }
                />
            </Card>
        );
    }

    if (isError) {
        return (
            <Card>
                <LoadingErrorMessage />
            </Card>
        );
    }

    return (
        <>
            {isLoading ? (
                <RowItem isLoading avatar={<span />} />
            ) : (
                <>
                    {data?.map((flexControl) => {
                        return (
                            <FlexControlItem
                                key={flexControl?.id}
                                flexControl={flexControl}
                                selectedCustomerId={selectedCustomerId}
                            />
                        );
                    })}
                </>
            )}
        </>
    );
};

export default Controls;
